import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/custom.css';

// Components
import Login from './components/Login';
import ResetPassword from './components/ResetPassword';
import DriverDashboard from './components/dashboard/DriverDashboard';
import AccountSettings from './components/AccountSettings';
import PrivateRoute from './components/PrivateRoute';
import ChangeEmail from './components/ChangeEmail';
import VerifyEmail from './components/VerifyEmail';
import SetPassword from './components/SetPassword';
import RootRedirect from './components/RootRedirect';
import SignUp from './components/SignUp';
import NewBookingLink from './components/admin/NewBookingLink';
import Checkout from './components/checkout/Checkout';
import ThankYou from './components/ThankYou';
import Leaderboard from './components/Leaderboard';
import LatestResults from './components/LatestResults';
import TelemetryPage from './components/TelemetryPage';
import PublicLapPage from './components/publicLap/PublicLapPage';
import BookSession from './components/booking/session/BookSession';
import RaceNights from './components/raceNights/RaceNights';
import DashboardNavbar from './components/shared/DashboardNavbar';
import SessionExpiredDialog from './components/SessionExpiredDialog';
import SimulatorAssignments from './components/SimulatorAssignments';

// Context
import { AuthProvider } from './context/AuthContext';
import FeatureRoute from './tenant/FeatureRoute';

/**
 * The old booking addresses now open Book a session. The query string goes with it: a 3DS card started
 * on the previous build returns to /booking/entitlement?guestBookingId=…, and that page's confirm reads
 * those params, so a bare <Navigate to="/book" replace /> would strand a charged member.
 */
function ToBook() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/book', search }} replace />;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <SessionExpiredDialog />
        <div className="App">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<FeatureRoute feature="signup" redirectTo="/login"><SignUp /></FeatureRoute>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify/:guid" element={<VerifyEmail />} />
            <Route path="/change-email/:changeId" element={<ChangeEmail />} />
            <Route path="/newpassword/:linkId" element={<SetPassword />} />
            <Route path="/admin/bookinglinks" element={<FeatureRoute feature="staffTools"><NewBookingLink /></FeatureRoute>} />
            <Route path="/checkout" element={<FeatureRoute feature="credits"><Checkout /></FeatureRoute>} />
            <Route path="/thank-you" element={<FeatureRoute feature="credits"><ThankYou /></FeatureRoute>} />
            <Route path="/leaderboard/:id" element={<FeatureRoute feature="raceData"><Leaderboard /></FeatureRoute>} />
            <Route path="/:type/leaderboard/:id" element={<Leaderboard />} />
            <Route path="/latest/:id" element={<FeatureRoute feature="raceData"><LatestResults /></FeatureRoute>} />
            {/* Public by design (spec D12): the post-session email links here, signed in or not. */}
            <Route path="/lap/:raceId/:lapId" element={<FeatureRoute feature="raceData" redirectTo="/login"><PublicLapPage /></FeatureRoute>} />
            <Route element={<DashboardNavbar />}>
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <DriverDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/settings"
              element={
                <PrivateRoute>
                  <AccountSettings />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/telemetry/:raceId/:lapId"
              element={
                <FeatureRoute feature="raceData">
                  <PrivateRoute>
                    <TelemetryPage />
                  </PrivateRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/book"
              element={
                <FeatureRoute feature="booking">
                  <PrivateRoute>
                    <BookSession />
                  </PrivateRoute>
                </FeatureRoute>
              }
            />
            <Route path="/booking" element={<ToBook />} />
            <Route path="/booking/entitlement" element={<ToBook />} />
            <Route
              path="/race-nights"
              element={
                <FeatureRoute feature="raceNights">
                  <PrivateRoute>
                    <RaceNights />
                  </PrivateRoute>
                </FeatureRoute>
              }
            />
            </Route>
            <Route path="/simulator-assignments/:location" element={<FeatureRoute feature="staffTools"><SimulatorAssignments /></FeatureRoute>} />
            <Route path="/" element={<RootRedirect />} />
          </Routes>
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
